import { client } from "@/client";
import { CustomToastComponent } from "@/components/shared";
import {
  FetchCarsOnTenderDocument,
  FetchPriceDetailsDocument,
  FetchPurchasesOnTenderDocument,
  FetchServicesOnTenderDocument,
  FetchTenderByIdDocument,
  RemoveServiceDocument,
  RemoveServiceMutation,
  RemoveServiceMutationVariables,
} from "@/graphql/__generated__/graphql";
import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

interface RemoveServiceCallOptions {
  disableRefetch?: boolean;
  disableToast?: boolean;
  refetchCars?: boolean;
  refetchPurchases?: boolean;
  onCompleted?: (_data: RemoveServiceMutation) => void;
  onError?: (_error: any) => void;
}

/**
 * Custom hook for removing a service from a tender and handling the necessary cache updates.
 * @param {number} serviceID - The ID of the service to be removed
 * @param {Object} options - Optional configuration options
 * @param {boolean} [options.disableRefetch=false] - Whether to disable refetching queries after mutation
 * @param {boolean} [options.refetchCars=false] - Whether to refetch cars queries after mutation (only useful when cars exist)
 * @param {boolean} [options.refetchPurchases=false] - Whether to refetch purchases queries after mutation (only useful when purchases exist)
 * @param {boolean} [options.disableToast=false] - Whether to disable showing success toast
 * @returns {Object}
 */
export default function useRemoveServiceMutationHook(
  serviceID: number,
  options?: {
    disableRefetch?: boolean;
    disableToast?: boolean;
    refetchCars?: boolean;
    refetchPurchases?: boolean;
  }
): {
  removeServiceMutation: any;
  loading: any;
  error: any;
} {
  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const [removeServiceMutate, { loading, error }] = useMutation<RemoveServiceMutation, RemoveServiceMutationVariables>(
    RemoveServiceDocument
  );

  /**
   * Wrapper around the Apollo mutate function that keeps refetchQueries defined in this hook,
   * while still letting callers decide (per call) whether cars/purchases should be refetched.
   */
  const removeService = (variables: RemoveServiceMutationVariables, callOptions: RemoveServiceCallOptions = {}) => {
    const shouldDisableRefetch = callOptions.disableRefetch ?? options?.disableRefetch ?? false;
    const shouldDisableToast = callOptions.disableToast ?? options?.disableToast ?? false;
    const shouldRefetchCars = callOptions.refetchCars ?? options?.refetchCars ?? false;
    const shouldRefetchPurchases = callOptions.refetchPurchases ?? options?.refetchPurchases ?? false;

    const tenderIdNumber = Number(tenderID);
    const serviceIdToEvict = serviceID || variables.serviceId;

    return removeServiceMutate({
      variables,
      onCompleted: (data) => {
        callOptions.onCompleted?.(data);

        if (!shouldDisableToast) {
          toast(CustomToastComponent, {
            toastId: "service-removed-success",
            data: {
              title: t("toasts.serviceremoved"),
              content: t("toasts.serviceremovedsuccessfully"),
              type: "success",
            },
          });
        }

        // Only evict from cache if refetch is NOT disabled
        // When disableRefetch is true (e.g., during bulk operations), we skip eviction
        // to prevent Apollo from automatically refetching dependent queries
        // before the operation completes. The cache will be updated by subsequent mutations.
        if (!shouldDisableRefetch) {
          client.cache.evict({
            id: `Service:${serviceIdToEvict}`,
          });
        }
      },
      onError: callOptions.onError,
      refetchQueries: shouldDisableRefetch
        ? []
        : [
          {
            query: FetchTenderByIdDocument,
            variables: { id: tenderIdNumber },
          },
          {
            query: FetchServicesOnTenderDocument,
            variables: { tenderId: tenderIdNumber },
          },
          // Refetch price details queries used in calculation overview
          // This ensures tender price details are updated when services are deleted
          {
            query: FetchPriceDetailsDocument,
            variables: {
              tenderId: tenderIdNumber,
              includeTenderPriceDetails: true,
              includePricingPerService: false,
              includeStandaloneCars: true,
              includeStandalonePurchases: true,
            },
          },
          {
            query: FetchPriceDetailsDocument,
            variables: {
              tenderId: tenderIdNumber,
              includeTenderPriceDetails: false,
              includePricingPerService: false,
              includeStandaloneCars: true,
              includeStandalonePurchases: true,
            },
          },
          ...(shouldRefetchCars
            ? [
              {
                query: FetchCarsOnTenderDocument,
                variables: { tenderId: tenderIdNumber },
              },
            ]
            : []),
          ...(shouldRefetchPurchases
            ? [
              {
                query: FetchPurchasesOnTenderDocument,
                variables: { tenderId: tenderIdNumber },
              },
            ]
            : []),
        ],
    });
  };

  return {
    removeServiceMutation: removeService,
    loading,
    error,
  };
}
